// src/utils/strapi-normalizer.ts
// All data transformation and normalization logic

import {
    ID,
    StrapiSingleItem,
    StrapiMultipleItems,
    StrapiResponse,
    StrapiSingleResponse,
    StrapiMultipleResponse,
    StrapiMedia,
    StrapiRelationOne,
    StrapiRelationMany,
    BaseModel,
    STRAPI_CONSTANTS
} from '@/types/common';

/**
 * Raw Strapi data types for normalization
 */
interface StrapiRawItem<T> {
    id: ID;
    documentId?: string;
    attributes: T;
}

interface StrapiRawData<T> {
    data: StrapiRawItem<T> | StrapiRawItem<T>[] | null;
    meta?: any;
}

interface StrapiRawResponse<T> {
    data: StrapiRawItem<T> | StrapiRawItem<T>[] | null;
    meta: {
        pagination?: any;
        [key: string]: any;
    };
}

/**
 * Normalization options
 */
interface NormalizationOptions {
    includeMetadata?: boolean;
    preserveRawStructure?: boolean;
    validateData?: boolean;
    handleErrors?: 'throw' | 'warn' | 'ignore';
}

/**
 * Strapi Data Normalizer
 * Transforms raw Strapi API responses into clean, typed objects
 */
export class StrapiNormalizer {
    private static defaultOptions: NormalizationOptions = {
        includeMetadata: false,
        preserveRawStructure: false,
        validateData: true,
        handleErrors: 'warn'
    };

    /**
     * ✅ Main normalization entry point (KEEP EXISTING FUNCTION NAME)
     * Handles both single items and arrays
     */
    public static normalizeStrapi<T>(
        data: any,
        options: NormalizationOptions = {}
    ): T | T[] | null {
        const opts = { ...this.defaultOptions, ...options };

        try {
            if (!data) {
                return null;
            }

            // Handle array of items
            if (Array.isArray(data)) {
                return data
                    .map(item => this.normalizeStrapiItem<T>(item, opts))
                    .filter((item): item is T => item !== null);
            }

            // Handle single item
            return this.normalizeStrapiItem<T>(data, opts);
        } catch (error) {
            return this.handleNormalizationError(error, 'normalizeStrapi', opts);
        }
    }

    /**
     * ✅ Normalize Strapi response (KEEP EXISTING FUNCTION NAME)
     * Handles complete API responses with meta
     */
    public static normalizeStrapiResponse<T>(
        response: StrapiRawResponse<T> | null,
        options: NormalizationOptions = {}
    ): { data: T | T[] | null; meta?: any } {
        const opts = { ...this.defaultOptions, ...options };

        try {
            if (!response) {
                return { data: null, meta: {} };
            }

            const normalizedData = this.normalizeStrapi<T>(response.data, opts);

            return {
                data: normalizedData,
                ...(opts.includeMetadata && { meta: response.meta })
            };
        } catch (error) {
            this.handleNormalizationError(error, 'normalizeStrapiResponse', opts);
            return { data: null, meta: {} };
        }
    }

    /**
     * ✅ Parse Strapi response (KEEP EXISTING FUNCTION NAME)
     * More detailed parsing with success status
     */
    public static parseStrapiResponse<T>(
        response: any,
        options: NormalizationOptions = {}
    ): {
        data: T | T[] | null;
        meta: any;
        pagination?: any;
        isSuccess: boolean;
        normalizedAt: string;
    } {
        const opts = { ...this.defaultOptions, ...options };

        try {
            const isSuccess = response?.status >= 200 && response?.status < 300;
            const responseData = response?.data || response;

            return {
                data: this.normalizeStrapi<T>(responseData?.data, opts),
                meta: responseData?.meta || {},
                pagination: responseData?.meta?.pagination,
                isSuccess,
                normalizedAt: new Date().toISOString()
            };
        } catch (error) {
            this.handleNormalizationError(error, 'parseStrapiResponse', opts);
            return {
                data: null,
                meta: {},
                isSuccess: false,
                normalizedAt: new Date().toISOString()
            };
        }
    }

    /**
     * Format data for sending to Strapi API
     */
    public static formatRecordForStrapi<T extends Partial<BaseModel>>(
        data: T,
        options: { includeTimestamps?: boolean } = {}
    ): { data: T & { updatedAt?: string; createdAt?: string } } {
        const { includeTimestamps = true } = options;

        const formattedData = { ...data };

        if (includeTimestamps) {
            formattedData.updatedAt = new Date().toISOString();
            if (!formattedData.createdAt) {
                formattedData.createdAt = new Date().toISOString();
            }
        }

        // Remove fields that shouldn't be sent to API
        delete (formattedData as any).id;
        delete (formattedData as any).documentId;

        return { data: formattedData };
    }

    /**
     * Normalize media/file fields
     */
    public static normalizeMedia(mediaData: any): StrapiMedia | StrapiMedia[] | null {
        if (!mediaData || !mediaData.data) {
            return null;
        }

        if (Array.isArray(mediaData.data)) {
            return mediaData.data.map((item: any) => ({
                id: item.id,
                documentId: item.documentId,
                ...item.attributes
            }));
        }

        return {
            id: mediaData.data.id,
            documentId: mediaData.data.documentId,
            ...mediaData.data.attributes
        };
    }

    /**
     * Normalize single relation (oneToOne, manyToOne)
     */
    public static normalizeRelationOne<T>(
        relationData: StrapiRelationOne<T> | any
    ): T | null {
        if (!relationData || !relationData.data) {
            return null;
        }

        return this.normalizeStrapiItem<T>(relationData.data);
    }

    /**
     * Normalize multiple relations (oneToMany, manyToMany)
     */
    public static normalizeRelationMany<T>(
        relationData: StrapiRelationMany<T> | any
    ): T[] {
        if (!relationData || !relationData.data || !Array.isArray(relationData.data)) {
            return [];
        }

        return relationData.data
            .map((item: any) => this.normalizeStrapiItem<T>(item))
            .filter((item: T | null): item is T => item !== null);
    }

    /**
     * Extract nested relations and normalize them
     */
    public static normalizeNestedRelations<T extends Record<string, any>>(
        data: T,
        options: NormalizationOptions = {}
    ): T {
        const normalized = { ...data };

        Object.keys(normalized).forEach(key => {
            const value = normalized[key];

            if (value && typeof value === 'object') {
                if (value.data !== undefined) {
                    // This looks like a Strapi relation
                    if (Array.isArray(value.data)) {
                        normalized[key] = this.normalizeRelationMany(value);
                    } else {
                        normalized[key] = this.normalizeRelationOne(value);
                    }
                } else if (Array.isArray(value)) {
                    // Array of items that might need normalization
                    normalized[key] = value.map(item => {
                        if (item && typeof item === 'object' && item.id && item.attributes) {
                            return this.normalizeStrapiItem(item, options);
                        }
                        return item;
                    });
                }
            }
        });

        return normalized;
    }

    /**
     * Validate normalized data structure
     */
    public static validateNormalizedData<T>(data: T, schema?: string): boolean {
        try {
            if (!data || typeof data !== 'object') {
                return false;
            }

            // Basic validation - must have an id
            const hasId = (data as any).id !== undefined;

            if (!hasId) {
                console.warn(`⚠️ Normalized data missing required 'id' field${schema ? ` for ${schema}` : ''}`);
                return false;
            }

            return true;
        } catch (error) {
            console.error('❌ Data validation failed:', error);
            return false;
        }
    }

    /**
     * Create empty normalized structure
     */
    public static createEmptyResponse<T>(): { data: T | null; meta: any } {
        return {
            data: null,
            meta: {}
        };
    }

    /**
     * Batch normalize multiple responses
     */
    public static batchNormalize<T>(
        responses: any[],
        options: NormalizationOptions = {}
    ): Array<{ data: T | T[] | null; meta: any; index: number }> {
        return responses.map((response, index) => ({
            ...this.normalizeStrapiResponse<T>(response, options),
            index
        }));
    }

    // Private helper methods

    /**
     * ✅ Normalize single Strapi item (KEEP EXISTING FUNCTION NAME)
     */
    private static normalizeStrapiItem<T>(
        item: any,
        options: NormalizationOptions = {}
    ): T | null {
        if (!item) {
            return null;
        }

        try {
            // Already normalized?
            if (item.id && !item.attributes) {
                return item as T;
            }

            // Strapi v5 format: { id, documentId, attributes }
            if (item.id && item.attributes) {
                const normalized = {
                    id: item.id,
                    ...(item.documentId && { documentId: item.documentId }),
                    ...item.attributes
                } as T;

                // Normalize nested relations
                const withRelations = this.normalizeNestedRelations(normalized, options);

                // Validate if requested
                if (options.validateData && !this.validateNormalizedData(withRelations)) {
                    return null;
                }

                return withRelations;
            }

            // Handle edge cases
            if (typeof item === 'object' && item !== null) {
                return item as T;
            }

            return null;
        } catch (error) {
            return this.handleNormalizationError(error, 'normalizeStrapiItem', options);
        }
    }

    /**
     * Handle normalization errors based on options
     */
    private static handleNormalizationError<T>(
        error: any,
        operation: string,
        options: NormalizationOptions
    ): T | null {
        const errorMessage = `Normalization error in ${operation}: ${error.message}`;

        switch (options.handleErrors) {
            case 'throw':
                throw new Error(errorMessage);
            case 'warn':
                console.warn(`⚠️ ${errorMessage}`);
                break;
            case 'ignore':
            default:
                // Silent fail
                break;
        }

        return null;
    }

    /**
     * Deep clone object safely
     */
    private static safeDeepClone<T>(obj: T): T {
        try {
            return structuredClone(obj);
        } catch {
            // Fallback for older environments
            return JSON.parse(JSON.stringify(obj));
        }
    }

    /**
     * Check if object has valid Strapi structure
     */
    private static isValidStrapiStructure(obj: any): boolean {
        return obj &&
            typeof obj === 'object' &&
            obj.id !== undefined &&
            (obj.attributes !== undefined || obj.documentId !== undefined);
    }
}

// Legacy function exports for backward compatibility

/**
 * ✅ Legacy function - maintain existing signature
 */
export const normalizeStrapi = <T>(data: any): T | T[] | null => {
    return StrapiNormalizer.normalizeStrapi<T>(data);
};

/**
 * ✅ Legacy function - maintain existing signature
 */
export const normalizeStrapiResponse = <T>(response: any): { data: T | T[] | null; meta: any } => {
    return StrapiNormalizer.normalizeStrapiResponse<T>(response);
};

/**
 * ✅ Legacy function - maintain existing signature
 */
export const parseStrapiResponse = <T>(response: any) => {
    return StrapiNormalizer.parseStrapiResponse<T>(response);
};

/**
 * ✅ Legacy function - maintain existing signature
 */
export const formatRecordForStrapi = <T extends Partial<BaseModel>>(data: T) => {
    return StrapiNormalizer.formatRecordForStrapi(data);
};

// Additional utility exports
export const normalizeMedia = (mediaData: any) => StrapiNormalizer.normalizeMedia(mediaData);
export const normalizeRelationOne = <T>(relationData: any) => StrapiNormalizer.normalizeRelationOne<T>(relationData);
export const normalizeRelationMany = <T>(relationData: any) => StrapiNormalizer.normalizeRelationMany<T>(relationData);


// Default export for convenience
export default {
    // Main normalizer class
    StrapiNormalizer,

    // Legacy functions
    normalizeStrapi,
    normalizeStrapiResponse,
    parseStrapiResponse,
    formatRecordForStrapi,

    // Utility functions
    normalizeMedia,
    normalizeRelationOne,
    normalizeRelationMany
};
